import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  hasWearableBiometricsToday,
  isMorningBriefingComplete,
  isMorningCheckInComplete,
  isMorningFlowComplete,
  morningFlowSequence,
  resolveActiveMorningStep,
  type MorningFlowStep,
} from '../lib/morningFlow'
import {
  markMorningCheckInSessionComplete,
  readMorningCheckInSession,
  type MorningCheckInSessionRecord,
} from '../lib/morningCheckInSession'
import type { CoachHealthSummary } from '../lib/coachHealthTypes'
import { briefingKeyMorning, markBriefingRead } from '../lib/coachBriefingReadState'
import { clientLocalDate } from '../lib/clientContext'
import { submitMorningWellness, type WellnessPillars } from '../lib/submitMorningWellness'
import type { TodayProtocolResponse } from '../lib/dailyOverrideTypes'

type UseMorningFlowOptions = {
  summary: CoachHealthSummary | null
  todayProtocol: TodayProtocolResponse | null
  showMorningBrief: boolean
  hasMorningText: boolean
  onDispatched?: () => void
}

export function useMorningFlow({
  summary,
  todayProtocol,
  showMorningBrief,
  hasMorningText,
  onDispatched,
}: UseMorningFlowOptions) {
  const todayId = clientLocalDate()
  const hasWearable = hasWearableBiometricsToday(summary)
  const serverCheckInDone = isMorningCheckInComplete(todayProtocol)

  const [localCheckInDone, setLocalCheckInDone] = useState(false)
  const [sessionRecord, setSessionRecord] = useState<MorningCheckInSessionRecord | null>(() =>
    readMorningCheckInSession(todayId),
  )
  const [pendingPillars, setPendingPillars] = useState<WellnessPillars | null>(null)
  const [briefingAcknowledged, setBriefingAcknowledged] = useState(false)
  const [dispatching, setDispatching] = useState(false)
  const [dispatchError, setDispatchError] = useState<string | null>(null)

  const justCompletedThisSession = sessionRecord != null
  const briefingComplete =
    isMorningBriefingComplete(hasWearable, showMorningBrief, hasMorningText) || briefingAcknowledged
  const checkInComplete = serverCheckInDone || localCheckInDone

  const sequence = useMemo(() => morningFlowSequence(hasWearable), [hasWearable])

  const activeStep: MorningFlowStep | null = useMemo(() => {
    if (isMorningFlowComplete(checkInComplete, briefingComplete)) return null
    return resolveActiveMorningStep(sequence, checkInComplete, briefingComplete)
  }, [sequence, checkInComplete, briefingComplete])

  const showMorningCheckIn =
    activeStep === 'check_in' || (justCompletedThisSession && checkInComplete)

  const flowEnabled = activeStep != null || (pendingPillars != null && !serverCheckInDone)

  const acknowledgeBriefing = useCallback(() => {
    markBriefingRead(briefingKeyMorning(todayId))
    setBriefingAcknowledged(true)
  }, [todayId])

  const finishBriefing = useCallback(() => {
    acknowledgeBriefing()
  }, [acknowledgeBriefing])

  const completeCheckIn = useCallback((pillars: WellnessPillars) => {
    setPendingPillars(pillars)
    setLocalCheckInDone(true)
    setSessionRecord(markMorningCheckInSessionComplete())
  }, [])

  const dispatchReadiness = useCallback(async (pillars: WellnessPillars) => {
    setDispatching(true)
    setDispatchError(null)
    const { error } = await submitMorningWellness(pillars)
    setDispatching(false)
    if (error) {
      setDispatchError(error)
      return false
    }
    setPendingPillars(null)
    onDispatched?.()
    return true
  }, [onDispatched])

  useEffect(() => {
    if (serverCheckInDone || !pendingPillars || !briefingComplete || dispatching) return
    void dispatchReadiness(pendingPillars)
  }, [serverCheckInDone, pendingPillars, briefingComplete, dispatching, dispatchReadiness])

  return {
    activeStep,
    flowEnabled,
    checkInComplete,
    briefingComplete,
    justCompletedThisSession,
    showMorningCheckIn,
    sessionCompletedAt: sessionRecord?.completedAt ?? null,
    dispatching,
    dispatchError,
    pendingPillars,
    completeCheckIn,
    acknowledgeBriefing,
    finishBriefing,
    hasWearable,
  }
}
